import React, { useState } from 'react';
import { Row, Col, Alert, Tab, Tabs } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { getCompareSummaryReport } from '../../api/SummaryReport';
import { IMonthValue } from '../../util/DateUtils';
import { WaitModal } from '../../components/ReportModals';
import CompareMetricsTable, { ICompareTableMetrics, ICompareTableMetricsRow } from './CompareMetricsTable';
import CompareReportTable, { ICompareReportTableRow } from './CompareReportTable';
import CompareFeeTable, { ICompareFeeTableRow } from './CompareFeeTable';
import DateSelection from './DateSelection';
import RatiosTable, { IRatiosTableRow } from './RatiosTable';

interface ICompareReportOptionsProps {
  intl: any;
}

const CompareReports = (props: ICompareReportOptionsProps) => {
  const [modalWaitOpen, setWaitModalOpen] = useState(false);
  const [tab, setTab] = useState("report");
  const [tableData, setTableData] = useState([] as ICompareReportTableRow[]);
  const [compareData, setCompareData] = useState([] as ICompareReportTableRow[]);
  const [feeData, setFeeData] = useState([] as ICompareFeeTableRow[]);
  const [feeHeader, setFeeHeader] = useState("");
  const [feeSumData, setFeeSumData] = useState([] as ICompareFeeTableRow[]);
  const [feeClearingData, setFeeClearingData] = useState([] as ICompareFeeTableRow[]);
  const [compareFeeData, setCompareFeeData] = useState([] as ICompareFeeTableRow[]);
  const [compareFeeHeader, setCompareFeeHeader] = useState("");
  const [compareFeeSumData, setCompareFeeSumData] = useState([] as ICompareFeeTableRow[]);
  const [compareFeeClearingData, setCompareFeeClearingData] = useState([] as ICompareFeeTableRow[]);
  const [metrics, setMetrics] = useState({} as ICompareTableMetrics);
  const [compareMetrics, setCompareMetrics] = useState({} as ICompareTableMetrics);
  const [ratios, setRatios] = useState([] as IRatiosTableRow[]);
  const [header, setHeader] = useState("");
  const [compareHeader, setCompareHeader] = useState("");
  const [metricsHeader, setMetricsHeader] = useState("");
  const [compareMetricsHeader, setCompareMetricsHeader] = useState("");
  const [ratiosHeader, setRatiosHeader] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState('');

  const getData = async (header: string, start: IMonthValue, end: IMonthValue, compare = false) => {
    setWaitModalOpen(true);
    setShowAlert(false);

    getCompareSummaryReport(tab, start, end).then((data) => {
      if (!data || data.error) {
        let text = "";
        if(data.error.errorID) {
          text += props.intl.formatMessage({id: `summaryreport.error.${data.error.errorID}`});
        }
        if(data.error.value) {
          text += data.error.value;
        }
        if(text==="") {
          text = props.intl.formatMessage({id: 'summaryreport.noreportsfound'});
        }
        setAlertText(text);
        setShowAlert(true);
        return;
      }
      if(tab==="report") {
        reportData(data, compare, header);
      } else if(tab==="fee") {
        feeReportData(data, compare, header);
        feeReportSumData(data, compare);
        feeReportClearingData(data, compare);
      } else if(tab==="metrics") {
        metricsData(data, compare, header);
      } else if(tab==="ratios") {
        ratiosData(data, header);
      }
    }).finally(() => {
      setWaitModalOpen(false);
    });
  }

  const reportData = (data: any, compare: boolean, header: string ) => {
    if (!data.REPORTROWS) {
      setAlertText(props.intl.formatMessage({id: 'summaryreport.noreportsfound'}));
      setShowAlert(true);
      return;
    }
    const totals = {
      description: 'totals',
      kg: 0,
      handling: 0,
      transport: 0,
      receiving: 0,
      containers: 0,
      eur: 0,
      eurKg: 0
    };
    // read REPORTROWS to a map and group it according to REPORTGROUPING
    const rows = {} as {[key: string]: ICompareReportTableRow};
    const jsonRows = data.REPORTROWS;
    Object.keys(data.REPORTGROUPING).forEach((groupKey: string) => {
      const group = data.REPORTGROUPING[groupKey];
      Object.keys(group).forEach((key: string) => {
        group[key].forEach((subclass: string) => {
          const rowKey = groupKey + key;
          const eur = parseFloat(jsonRows[subclass].TOTAL_EUR);
          if (!rows[rowKey]) {
            rows[rowKey] = {
              description: rowKey, // include also weee info
              kg: parseFloat(jsonRows[subclass].KG),
              handling: parseFloat(jsonRows[subclass].HANDLING_EUR),
              transport: parseFloat(jsonRows[subclass].TRANSPORT_EUR),
              receiving: parseFloat(jsonRows[subclass].RECEIVING_EUR),
              containers: parseFloat(jsonRows[subclass].CONTAINERS_EUR),
              eur,
              eurKg: parseFloat(jsonRows[subclass]['EUR/KG']),
            };
          } else {
            rows[rowKey].kg += parseFloat(jsonRows[subclass].KG);
            rows[rowKey].handling += parseFloat(jsonRows[subclass].HANDLING_EUR);
            rows[rowKey].transport += parseFloat(jsonRows[subclass].TRANSPORT_EUR);
            rows[rowKey].receiving += parseFloat(jsonRows[subclass].RECEIVING_EUR);
            rows[rowKey].containers += parseFloat(jsonRows[subclass].CONTAINERS_EUR);
            rows[rowKey].eur += eur;
            rows[rowKey].eurKg = rows[rowKey].kg > 0 ?
              rows[rowKey].eur/rows[rowKey].kg : 0;
          }
          totals.kg += parseFloat(jsonRows[subclass].KG);
          totals.handling += parseFloat(jsonRows[subclass].HANDLING_EUR);
          totals.transport  += parseFloat(jsonRows[subclass].TRANSPORT_EUR);
          totals.receiving += parseFloat(jsonRows[subclass].RECEIVING_EUR);
          totals.containers += parseFloat(jsonRows[subclass].CONTAINERS_EUR);
          totals.eur += eur;
        })
      })
    });
    totals.eurKg = totals.eur / totals.kg;

    const table = [] as ICompareReportTableRow[];
    Object.keys(rows).forEach((key) => table.push(rows[key]));
    table.push(totals);
    if(compare) {
      setCompareData(table);
      setCompareHeader(header);
    } else {
      setTableData(table);
      setHeader(header);
    }
  }

  const feeReportData = (data: any, compare: boolean, header: string ) => {
    if (!data.REPORTROWS) {
      setAlertText(props.intl.formatMessage({id: 'summaryreport.noreportsfound'}));
      setShowAlert(true);
      return;
    }
    const totals = {
      description: 'totals',
      kg: 0,
      eur: 0,
      eurKg: 0,
      pomKg: 0,
      pomKgRate: 0,
      fee: 0,
      feeEurKg: 0,
      deviation: 0
    };
    // read REPORTROWS to a map and group it according to REPORTGROUPING
    const rows = {} as {[key: string]: ICompareFeeTableRow};
    const jsonRows = data.REPORTROWS;
    Object.keys(data.REPORTGROUPING).forEach((groupKey: string) => {
      const group = data.REPORTGROUPING[groupKey];
      Object.keys(group).forEach((key: string) => {
        group[key].forEach((subclass: string) => {
          const rowKey = groupKey + key;
          const eur = parseFloat(jsonRows[subclass].TOTAL_EUR);
          if (!rows[rowKey]) {
            rows[rowKey] = {
              description: rowKey, // include also weee info
              kg: parseFloat(jsonRows[subclass].KG),
              eur,
              eurKg: parseFloat(jsonRows[subclass]['EUR/KG']),
              pomKg: parseFloat(jsonRows[subclass].POM_KG),
              pomKgRate: parseFloat(jsonRows[subclass]['KG/POM_KG']),
              fee: parseFloat(jsonRows[subclass].FEES_EUR),
              feeEurKg: parseFloat(jsonRows[subclass]['FEES_EUR/KG']),
              deviation: parseFloat(jsonRows[subclass].DEV),
            };
          } else {
            rows[rowKey].kg += parseFloat(jsonRows[subclass].KG);
            rows[rowKey].eur += eur;
            rows[rowKey].eurKg = rows[rowKey].kg > 0 ?
              rows[rowKey].eur/rows[rowKey].kg : 0;
            rows[rowKey].pomKg += parseFloat(jsonRows[subclass].POM_KG);
            rows[rowKey].pomKgRate = rows[rowKey].pomKg > 0 ? Math.round(rows[rowKey].kg / rows[rowKey].pomKg * 100) : 0;
            rows[rowKey].fee += parseFloat(jsonRows[subclass].FEES_EUR);
            rows[rowKey].feeEurKg = rows[rowKey].kg > 0 ? rows[rowKey].fee / rows[rowKey].kg : 0;
            rows[rowKey].deviation = rows[rowKey].feeEurKg - rows[rowKey].eurKg;
          }
          totals.kg += parseFloat(jsonRows[subclass].KG);
          totals.pomKg += parseFloat(jsonRows[subclass].POM_KG);
          totals.fee  += parseFloat(jsonRows[subclass].FEES_EUR);
          totals.eur += eur;
        })
      })
    });
    totals.eurKg = totals.kg !== 0 ? totals.eur / totals.kg : 0;
    totals.pomKgRate = totals.pomKg !== 0 ? Math.round((totals.kg / totals.pomKg) * 100) : 0;
    totals.feeEurKg = totals.fee / totals.kg;
    totals.deviation = totals.feeEurKg - totals.eurKg;

    const table = [] as ICompareFeeTableRow[];
    Object.keys(rows).forEach((key) => table.push(rows[key]));
    table.push(totals);
    if(compare) {
      setCompareFeeData(table);
      setCompareFeeHeader(header);
    } else {
      setFeeData(table);
      setFeeHeader(header);
    }
  }

  const feeReportSumData = (data: any, compare: boolean ) => {
    if (!data.REPORTROWS) {
      setAlertText(props.intl.formatMessage({id: 'summaryreport.noreportsfound'}));
      setShowAlert(true);
      return;
    }
    let table = getMappedFeeTableData(data.REPORTROWS, data.REPORTSUMGROUPING);
    if(compare) {
      setCompareFeeSumData(table);
    } else {
      setFeeSumData(table);
    }
  }

  const feeReportClearingData = (data: any, compare: boolean ) => {
    if (!data.REPORTROWS) {
      setAlertText(props.intl.formatMessage({id: 'summaryreport.noreportsfound'}));
      setShowAlert(true);
      return;
    }
    let table = getMappedFeeTableData(data.REPORTROWS, data.REPORTCLEARING);
    if(compare) {
      setCompareFeeClearingData(table);
    } else {
      setFeeClearingData(table);
    }
  }

  const getMappedFeeTableData = (jsonRows: any, groupingData: any ) => {
    const totals = {
      description: 'totals',
      kg: 0,
      eur: 0,
      eurKg: 0,
      pomKg: 0,
      pomKgRate: 0,
      fee: 0,
      feeEurKg: 0,
      deviation: 0
    };
    // read REPORTROWS to a map and group it according to groupingData
    const rows = {} as {[key: string]: ICompareFeeTableRow};
    Object.keys(groupingData).forEach((groupingKey: string) => {
      const groupCodes = groupingData[groupingKey];
      groupCodes.forEach((code: string) => {
        const rowKey = groupingKey;
        const eur = parseFloat(jsonRows[code].TOTAL_EUR);
        if (!rows[rowKey]) {
          rows[rowKey] = {
            description: rowKey,
            kg: parseFloat(jsonRows[code].KG),
            eur,
            eurKg: parseFloat(jsonRows[code]['EUR/KG']),
            pomKg: parseFloat(jsonRows[code].POM_KG),
            pomKgRate: parseFloat(jsonRows[code]['KG/POM_KG']),
            fee: parseFloat(jsonRows[code].FEES_EUR),
            feeEurKg: parseFloat(jsonRows[code]['FEES_EUR/KG']),
            deviation: parseFloat(jsonRows[code].DEV),
          };
        } else {
          rows[rowKey].kg += parseFloat(jsonRows[code].KG);
          rows[rowKey].eur += eur;
          rows[rowKey].eurKg = rows[rowKey].kg > 0 ?
            rows[rowKey].eur/rows[rowKey].kg : 0;
          rows[rowKey].pomKg += parseFloat(jsonRows[code].POM_KG);
          rows[rowKey].pomKgRate = rows[rowKey].pomKg > 0 ? Math.round(rows[rowKey].kg / rows[rowKey].pomKg * 100) : 0;
          rows[rowKey].fee += parseFloat(jsonRows[code].FEES_EUR);
          rows[rowKey].feeEurKg = rows[rowKey].kg > 0 ? rows[rowKey].fee / rows[rowKey].kg : 0;
          rows[rowKey].deviation = rows[rowKey].feeEurKg - rows[rowKey].eurKg;
        }
        totals.kg += parseFloat(jsonRows[code].KG);
        totals.pomKg += parseFloat(jsonRows[code].POM_KG);
        totals.fee  += parseFloat(jsonRows[code].FEES_EUR);
        totals.eur += eur;
      })
    });
    totals.eurKg = totals.kg !== 0 ? totals.eur / totals.kg : 0;
    totals.pomKgRate = totals.pomKg !== 0 ? Math.round((totals.kg / totals.pomKg) * 100) : 0;
    totals.feeEurKg = totals.fee / totals.kg;
    totals.deviation = totals.feeEurKg - totals.eurKg;

    const table = [] as ICompareFeeTableRow[];
    Object.keys(rows).forEach((key) => table.push(rows[key]));
    table.push(totals);
    return table;
  }

  const metricsData = (data: any, compare: boolean, header: string) => {
    if (!data.TOTAL_INCOME &&
        !data.HANDLING &&
        !data.TRANSPORT &&
        !data.RECEIVING &&
        !data.CONTAINERS &&
        !data.TOTAL_COST &&
        !data.METAL_REFUNDS &&
        !data.RECYCLE_FEES) {
      setAlertText(props.intl.formatMessage({id: 'summaryreport.noreportsfound'}));
      setShowAlert(true);
      return;
    }
    const table = {} as ICompareTableMetrics;
    table.handling = parseMetricData(data.HANDLING);
    table.transport = parseMetricData(data.TRANSPORT);
    table.receiving = parseMetricData(data.RECEIVING);
    table.containers = parseMetricData(data.CONTAINERS);
    table.total_cost = parseMetricData(data.TOTAL_COST);
    table.metal_refunds = parseMetricData(data.METAL_REFUNDS);
    table.recycle_fees = parseMetricData(data.RECYCLE_FEES);
    table.total_income = {eur: data.TOTAL_INCOME} as ICompareTableMetricsRow;

    if(compare) {
      setCompareMetrics(table);
      setCompareMetricsHeader(header);
    } else {
      setMetrics(table);
      setMetricsHeader(header);
    }
  }

  const parseMetricData = ( data:any ) => {
    let row = {
      eur: data.EUR ? data.EUR : 0,
      eurKg: (data['EUR/KG'] !== null && data['EUR/KG'] !== 'undefined') ? data['EUR/KG'] : null,
      avg_metric: (data.AVG_METRIC !== null && data.AVG_METRIC !== undefined) ? data.AVG_METRIC : null,
      pct: (data.PCT !== null && data.PCT !== undefined) ? data.PCT : null
    } as ICompareTableMetricsRow;
    return row;
  }

  const ratiosData = (data: any, header: string) => {
    const table = [] as IRatiosTableRow[];

    const totals = {} as IRatiosTableRow;
    totals.totals = true;
    totals.handling_per_income = 0;
    totals.transport_per_income = 0;
    totals.receiving_per_income = 0;
    totals.recycling_per_cost = 0;
    totals.recycling_per_income = 0;
    totals.refund_per_income = 0;
    totals.cost_per_income = 0;
    totals.handling = 0;
    totals.transport = 0;
    totals.receiving = 0;
    totals.containers = 0;
    totals.cost = 0;
    totals.metal_refund = 0;
    totals.recycling_fee = 0;
    totals.income = 0;

    Object.keys(data).forEach((key: string) => {
      if(key!=="error") {
        const row = {} as IRatiosTableRow;
        row.month = key;
        row.handling_per_income = data[key].HANDLING_PER_INCOME;
        row.transport_per_income = data[key].TRANSPORT_PER_INCOME;
        row.receiving_per_income = data[key].RECEIVING_PER_INCOME;
        row.recycling_per_cost = data[key].RECYCLING_PER_COST;
        row.recycling_per_income = data[key].RECYCLING_PER_INCOME;
        row.refund_per_income = data[key].REFUND_PER_INCOME;
        row.cost_per_income = data[key].COST_PER_INCOME;
        row.handling = data[key].RAW.HANDLING;
        row.transport = data[key].RAW.TRANSPORT;
        row.receiving = data[key].RAW.RECEIVING;
        row.containers = data[key].RAW.CONTAINERS;
        row.cost = data[key].RAW.COST;
        row.metal_refund = data[key].RAW.METAL_REFUND;
        row.recycling_fee = parseFloat(data[key].RAW.RECYCLING_FEE);
        row.income = data[key].RAW.INCOME;
        table.push(row);
        // Calculate totals
        totals.handling += row.handling;
        totals.transport += row.transport;
        totals.receiving += row.receiving;
        totals.containers += row.containers;
        totals.cost += row.cost;
        totals.metal_refund += row.metal_refund;
        totals.recycling_fee += row.recycling_fee;
        totals.income += row.income;
      }
    });

    // Make sure the oldest is first
    table.sort((a: IRatiosTableRow, b: IRatiosTableRow) => {
      if(a.month === b.month) return 0;
      let split = a.month.split('-');
      let ayear = parseInt(split[0]);
      let amonth = parseInt(split[1]);
      split = b.month.split('-');
      let byear = parseInt(split[0]);
      let bmonth = parseInt(split[1]);
      if(ayear > byear) return 1;
      if(ayear < byear) return -1;
      if(ayear===byear) {
        if(amonth > bmonth) return 1;
        if(amonth < bmonth) return -1;
      }
      return 1;
    });

    totals.handling_per_income = totals.handling/totals.income;
    totals.transport_per_income = totals.transport/totals.income;
    totals.receiving_per_income = totals.receiving/totals.income;
    totals.recycling_per_cost = totals.recycling_fee/totals.cost;
    totals.recycling_per_income = totals.recycling_fee/totals.income;
    totals.refund_per_income = totals.metal_refund/totals.income;
    totals.cost_per_income = totals.cost/totals.income;
    table.push(totals);

    setRatios(table);
    setRatiosHeader(header);
  }

  const handleSelectedTab = (eventKey : string) => {
    setTab(eventKey);
    setShowAlert(false);
  }

  return (
    <div>
      <WaitModal
        messageId="comparereport.getting"
        modalOpen={modalWaitOpen}
        setModalOpen={(open: boolean) => setWaitModalOpen(open)}
      />
      <h1>
        <FormattedMessage id="comparereports.header"/>
      </h1>
      <div className="settings">
        <Tabs defaultActiveKey={tab} id="comparereports-manage-tabs" onSelect={handleSelectedTab}>
          <Tab eventKey="report" title={props.intl.formatMessage({id:'comparereports.reports'})}>
            <Row>
              <Col lg="6">
                <DateSelection
                cssclass="comparison-radio-set radio-set report-set"
                header="comparereports.monthrange"
                click={(header, start, end) => { getData(header, start, end); }}
              />
              </Col>
                <Col lg="6">
                <DateSelection
                  cssclass="comparison-radio-set radio-set compare-set"
                  header="comparereports.comparerange"
                  click={(header, start, end) => { getData(header, start, end, true); }}
                />
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="fee" title={props.intl.formatMessage({id:'comparereports.fees'})}>
            <Row>
              <Col lg="6">
                <DateSelection
                cssclass="comparison-radio-set radio-set report-set"
                header="comparereports.monthrange"
                click={(header, start, end) => { getData(header, start, end); }}
              />
              </Col>
                <Col lg="6">
                <DateSelection
                  cssclass="comparison-radio-set radio-set compare-set"
                  header="comparereports.comparerange"
                  click={(header, start, end) => { getData(header, start, end, true); }}
                />
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="metrics" title={props.intl.formatMessage({id:'comparereports.metrics'})}>
            <Row>
              <Col lg="6">
                <DateSelection
                cssclass="comparison-radio-set radio-set report-set"
                  header="comparereports.monthrange"
                  click={(header, start, end) => { getData(header, start, end); }}
                />
              </Col>
              <Col lg="6">
                <DateSelection
                cssclass="comparison-radio-set radio-set compare-set"
                  header="comparereports.comparerange"
                  click={(header, start, end) => { getData(header, start, end, true); }}
                />
              </Col>
            </Row>
          </Tab>
          <Tab eventKey="ratios" title={props.intl.formatMessage({id:'comparereports.ratios'})}>
            <Row>
              <Col>
                <DateSelection
                  cssclass="comparison-radio-set radio-set report-set"
                  header="comparereports.monthrange"
                  click={(header, start, end) => { getData(header, start, end); }}
                />
              </Col>
            </Row>
          </Tab>
        </Tabs>
      </div>
      { showAlert &&
        <Alert variant="warning" onClose={() => setShowAlert(false)} dismissible={true}>
          { alertText }
        </Alert>
      }
      { tab === "report" &&
        ((tableData && tableData.length) > 0 ||
         (compareData && compareData.length > 0)) &&
        <CompareReportTable
          data={tableData}
          compareData={compareData}
          header={header}
          compareHeader={compareHeader}
        />
      }
      { tab === "fee" &&
        ((feeData && feeData.length) > 0 ||
         (compareFeeData && compareFeeData.length > 0)) &&
        <CompareFeeTable
          data={feeData}
          sumData={feeSumData}
          compareData={compareFeeData}
          compareSumData={compareFeeSumData}
          header={feeHeader}
          compareHeader={compareFeeHeader}
          clearingData={feeClearingData}
          compareClearingData={compareFeeClearingData}
        />
      }
      { tab === "metrics" &&
        (metrics.total_income || compareMetrics.total_income) &&
        <CompareMetricsTable
          data={metrics}
          compareData={compareMetrics}
          header={metricsHeader}
          compareHeader={compareMetricsHeader}
        />
      }
      { tab === "ratios" &&
        ratios.length > 0 &&
        <RatiosTable
          data={ratios}
          header={ratiosHeader}
        />
      }
    </div>
  )
}

export default injectIntl(CompareReports);
